import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Briefcase, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Provider } from "@/types";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { ContactButtons } from "@/components/ContactButtons";

const providerEditSchema = z.object({
  nombre: z.string().min(2, "Mínimo 2 caracteres"),
  apellidos: z.string().optional(),
  telefono: z.string().optional(),
});
type ProviderEditForm = z.infer<typeof providerEditSchema>;

const dateFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric" });

export function ProviderDetailPage() {
  const { id } = useParams();
  const providerId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmingToggle, setConfirmingToggle] = useState(false);

  const { data: provider, isLoading } = useQuery({
    queryKey: ["provider", providerId],
    queryFn: async () => (await api.get<Provider>(`/providers/${providerId}`)).data,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProviderEditForm>({ resolver: zodResolver(providerEditSchema) });

  useEffect(() => {
    if (!provider) return;
    reset({
      nombre: provider.nombre,
      apellidos: provider.apellidos ?? "",
      telefono: provider.telefono ?? "",
    });
  }, [provider, reset]);

  const saveProvider = useMutation({
    mutationFn: (data: ProviderEditForm) =>
      api.put(`/users/${providerId}`, {
        nombre: data.nombre,
        apellidos: data.apellidos || null,
        telefono: data.telefono || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider", providerId] });
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      toast.success("Proveedor actualizado");
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo actualizar el proveedor"),
  });

  const toggleActive = useMutation({
    mutationFn: () => api.put(`/users/${providerId}`, { active: !provider!.active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider", providerId] });
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      toast.success(provider!.active ? "Proveedor desactivado" : "Proveedor activado");
    },
    onError: () => toast.error("No se pudo actualizar el proveedor"),
  });

  if (isLoading || !provider) {
    return <p className="text-sm text-ink-400">Cargando proveedor…</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      <button
        onClick={() => navigate("/proveedores")}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-400 transition-colors hover:text-ink-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Proveedores
      </button>
      <Breadcrumbs items={[{ label: "Proveedores", to: "/proveedores" }, { label: `${provider.nombre} ${provider.apellidos ?? ""}`.trim() }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <Briefcase className="h-6 w-6" />
          </span>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-ink-900">
              {provider.nombre} {provider.apellidos}
              <ContactButtons telefono={provider.telefono} email={provider.email} />
            </h1>
            <p className="text-ink-400">
              {provider.email} — Alta: {dateFormatter.format(new Date(provider.createdAt))}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={provider.active ? "green" : "red"}>{provider.active ? "Activo" : "Inactivo"}</Badge>
          <Button className="w-24" onClick={() => setConfirmingToggle(true)}>
            {provider.active ? "Desactivar" : "Activar"}
          </Button>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit((data) => saveProvider.mutate(data))} className="flex flex-col gap-4">
          <p className="text-sm font-medium text-ink-600">Datos de contacto</p>
          <div className="grid grid-cols-2 gap-4">
            <Input id="provider-nombre" label="Nombre" error={errors.nombre?.message} {...register("nombre")} />
            <Input id="provider-apellidos" label="Apellidos" error={errors.apellidos?.message} {...register("apellidos")} />
          </div>
          <Input id="provider-telefono" label="Teléfono de contacto" placeholder="600 000 000" {...register("telefono")} />
          <div className="flex justify-end">
            <Button type="submit" disabled={saveProvider.isPending}>
              Guardar cambios
            </Button>
          </div>
        </form>
      </Card>

      <Card className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
          <MapPin className="h-4 w-4 text-brand-600" />
          Clubes ({provider.clubs.length})
        </p>
        {provider.clubs.length ? (
          <div className="flex flex-wrap gap-2">
            {provider.clubs.map((club) => (
              <button
                key={club.id}
                type="button"
                onClick={() => navigate(`/clubes/${club.id}`)}
                className="flex items-center gap-1.5 rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm font-medium text-ink-700 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50"
              >
                <MapPin className="h-3.5 w-3.5 text-brand-600" />
                {club.nombre}
                {!club.activo && <Badge tone="red">Inactivo</Badge>}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-400">
            Este proveedor todavía no tiene ningún club — desde su cuenta puede crear uno en "Clubes".
          </p>
        )}
      </Card>

      {confirmingToggle && (
        <ConfirmDialog
          open={confirmingToggle}
          onOpenChange={setConfirmingToggle}
          title={provider.active ? "Desactivar proveedor" : "Activar proveedor"}
          description={`¿Seguro que quieres ${provider.active ? "desactivar" : "activar"} a "${provider.nombre}"? ${
            provider.active ? "No podrá iniciar sesión (sus clubes y los usuarios de club siguen funcionando igual)." : ""
          }`}
          tone="primary"
          confirmLabel={provider.active ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActive.mutate()}
        />
      )}
    </div>
  );
}
